import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { dueChip } from './format.js';
import { isSafeHref } from './safeLink.js';
import type { Sphere } from './types.js';

/**
 * Small presentational pieces shared by the Life OS pages. They carry the
 * ADHD rules (spec §6) in one place: one bold headline per card, counts over
 * prose, and lists that collapse after three items.
 */

/** Title and sub sit together on the left; actions (children) on the right. */
export function PageHead({ title, sub, children }: { title: string; sub?: string; children?: ReactNode }): JSX.Element {
  return (
    <header className="page-head lo-head">
      <div className="lo-head__main">
        <h1 className="page-head__title">{title}</h1>
        {sub && <span className="page-head__sub">{sub}</span>}
      </div>
      {children}
    </header>
  );
}

export function Loading({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div className="lo-loading" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton lo-loading__row" />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }): JSX.Element {
  return (
    <div className="state state--error" role="alert">
      <div className="state__title">Couldn't load this</div>
      <div className="state__hint">{message}</div>
      {onRetry && (
        <button type="button" className="btn btn--ghost btn--sm lo-gap-top" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({ title, hint, children }: { title: string; hint?: string; children?: ReactNode }): JSX.Element {
  return (
    <div className="state lo-empty">
      <div className="state__title">{title}</div>
      {hint && <div className="state__hint">{hint}</div>}
      {children}
    </div>
  );
}

/** Loading → error → content, the order every page renders in. */
export function Loader<T>({
  state,
  children,
}: {
  state: { data: T | null; error: string | null; reload: () => void };
  children: (data: T) => ReactNode;
}): JSX.Element {
  if (state.data !== null) return <>{children(state.data)}</>;
  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  return <Loading />;
}

export function Dot({ color, label }: { color: string; label?: string }): JSX.Element {
  return (
    <span
      className="lo-dot"
      style={{ background: color }}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
    />
  );
}

/** Due chip: red only when overdue (spec §6). */
export function DueChip({ dueOn, today }: { dueOn: string | null; today: string }): JSX.Element | null {
  const chip = dueChip(dueOn, today);
  if (!chip) return null;
  return <span className={`lo-due lo-due--${chip.tone}`}>{chip.label}</span>;
}

export function Badge({ tone = 'muted', children }: { tone?: 'muted' | 'warn' | 'bad' | 'good' | 'accent'; children: ReactNode }): JSX.Element {
  return <span className={`lo-badge lo-badge--${tone}`}>{children}</span>;
}

/**
 * Shows the first `n` items and a "Show N more" button for the rest. `noun`
 * names the hidden items ('older', 'paused'); `one` is its singular when that
 * differs ('project' for 'projects'), so one hidden item never reads "1 projects".
 */
export function Collapsible<T>({
  items,
  n = 3,
  render,
  noun = 'more',
  one,
  inList = true,
}: {
  items: readonly T[];
  n?: number;
  render: (item: T, index: number) => ReactNode;
  noun?: string;
  one?: string;
  /** Rendered inside a <ul>/<ol>: the button gets its own <li>. */
  inList?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, n);
  const hidden = items.length - shown.length;
  const more =
    hidden > 0 ? (
      <button type="button" className="lo-more" onClick={() => setOpen(true)}>
        Show {hidden} {hidden === 1 && one ? one : noun}
      </button>
    ) : null;
  return (
    <>
      {shown.map(render)}
      {more && (inList ? <li className="lo-more-item">{more}</li> : more)}
    </>
  );
}

/** Round checkbox: the one primary action on a task row. */
export function Check({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      className={`lo-check${checked ? ' lo-check--on' : ''}`}
      onClick={onChange}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M3.5 8.5l3 3 6-7" />
      </svg>
    </button>
  );
}

/**
 * Segmented control over the existing `.seg` styles; scrolls sideways on a
 * phone. The selected segment is always scrolled into view (the wrapper
 * scrolls, never the window), and a fade on the right edge says there is more.
 */
export function Segmented<V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: V;
  options: readonly { value: V; label: string; count?: number }[];
  onChange: (v: V) => void;
}): JSX.Element {
  const wrap = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  const measure = useCallback((): void => {
    const w = wrap.current;
    if (w) setMore(w.scrollLeft + w.clientWidth < w.scrollWidth - 1);
  }, []);
  useLayoutEffect(() => {
    const w = wrap.current;
    const btn = w?.querySelector<HTMLElement>('.seg__btn--active');
    if (w && btn && w.scrollWidth > w.clientWidth) {
      w.scrollLeft = Math.max(0, btn.offsetLeft - (w.clientWidth - btn.offsetWidth) / 2);
    }
    measure();
  }, [value, options.length, measure]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);
  return (
    <div ref={wrap} className={`lo-segwrap${more ? ' lo-segwrap--more' : ''}`} onScroll={measure}>
      <div className="seg" role="tablist" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={o.value === value}
            className={`seg__btn${o.value === value ? ' seg__btn--active' : ''}`}
            onClick={() => onChange(o.value)}
          >
            {o.label}
            {o.count !== undefined && o.count > 0 && <span className="lo-seg__count">{o.count}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** "All" plus one chip per sphere; `value` null = all. `extra` chips join the same row. */
export function SphereFilter({
  spheres,
  value,
  onChange,
  extra,
}: {
  spheres: readonly Sphere[];
  value: string | null;
  onChange: (key: string | null) => void;
  extra?: ReactNode;
}): JSX.Element | null {
  if (spheres.length === 0) return extra ? <div className="lo-chips">{extra}</div> : null;
  return (
    <div className="lo-chips" role="group" aria-label="Sphere">
      <button
        type="button"
        className={`lo-chip${value === null ? ' lo-chip--on' : ''}`}
        aria-pressed={value === null}
        onClick={() => onChange(null)}
      >
        All
      </button>
      {spheres.map((s) => (
        <button
          key={s.key}
          type="button"
          className={`lo-chip${value === s.key ? ' lo-chip--on' : ''}`}
          aria-pressed={value === s.key}
          onClick={() => onChange(value === s.key ? null : s.key)}
        >
          <Dot color={s.color ?? 'var(--text-muted)'} />
          {s.name}
        </button>
      ))}
      {extra}
    </div>
  );
}

/** An external link only when the URL passes the §6 link rule; plain text otherwise. */
export function SafeLink({ href, children }: { href: string | null | undefined; children?: ReactNode }): JSX.Element | null {
  if (!href) return null;
  const text = children ?? href.replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (!isSafeHref(href)) return <span className="lo-link lo-link--plain">{text}</span>;
  return (
    <a className="lo-link" href={href} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );
}

/** A section title in the app's existing label style, with an optional count. */
export function SectionLabel({ children, count }: { children: ReactNode; count?: number }): JSX.Element {
  return (
    <h2 className="section-label lo-section-label">
      {children}
      {count !== undefined && <span className="lo-section-label__count">{count}</span>}
    </h2>
  );
}

/** Copies `text`; the label flips to "Copied" for two seconds. */
export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }): JSX.Element {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
    setTimeout(() => setState('idle'), 2000);
  }
  return (
    <button type="button" className="btn btn--ghost btn--sm lo-copy" onClick={() => void copy()}>
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Select and copy' : label}
    </button>
  );
}
